import React, { createContext, useCallback, useEffect } from 'react';
import { listTriggers, createTrigger, archiveTrigger } from '../../../../services';
import { useBaseContext, BASE_ACTIONS } from './useBaseContext';

const initialState = {
  data: [],
  loading: true,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 15
  },
  filters: {
    search: '',
    type: '',
    lastUpdate: '',
    state: '',
    owner: ''
  },
  mode: 'grid' 
};

const filterTriggers = (triggers, filters) => {
  let filtered = [...triggers];

  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(trigger => trigger.name && trigger.name.toLowerCase().includes(searchLower));
  }

  if (filters.type && filters.type.length > 0) {
    filtered = filtered.filter(trigger => 
      trigger.type && filters.type.includes(trigger.type)
    );
  }

  if (filters.state && filters.state.length > 0) {
    filtered = filtered.filter(trigger => 
      trigger.status && filters.state.includes(trigger.status)
    );
  }
  
  if (filters.owner && filters.owner.length > 0) {
    filtered = filtered.filter(trigger => 
      trigger.createdBy && filters.owner.includes(trigger.createdBy.toLowerCase().replace(' ', '_'))
    );
  }
  
  if (filters.lastUpdate) {
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const thisYear = new Date(now.getFullYear(), 0, 1);

    filtered = filtered.filter(trigger => {
      if (!trigger.lastUpdated) return false;
      const lastUpdated = new Date(trigger.lastUpdated);
      
      return (filters.lastUpdate === 'last_7_days' && lastUpdated >= sevenDaysAgo) ||
             (filters.lastUpdate === 'last_30_days' && lastUpdated >= thirtyDaysAgo) ||
             (filters.lastUpdate === 'this_year' && lastUpdated >= thisYear) ||
             filters.lastUpdate === 'all';
    });
  }

  return filtered;
};

const TriggersContext = createContext();


export const TriggersProvider = ({ children }) => {

  const baseContext = useBaseContext(
    initialState,
    listTriggers,
    filterTriggers,
    'triggers'
  );

  const { dispatch, setPage, setItemsPerPage, reloadData } = baseContext;

  useEffect(() => {
    baseContext.loadItems();
  }, []);

  // Triggers-specific actions
  const createTriggerAction = useCallback(async (triggerData, tenantUuid) => {
    try {
      dispatch({ type: BASE_ACTIONS.SET_LOADING, payload: true });

      const response = await createTrigger({
        tenantId: tenantUuid,
        name: triggerData.name,
        description: triggerData.description
      });

      if (response.success) {
        // Create a trigger object with essential UI fields
        const newTrigger = {
          uuid: response.data.trigger.uuid,
          id: response.data.trigger.uuid, // Add id field as well
          name: response.data.trigger.name,
          nameText: response.data.trigger.name,
          description: triggerData.description,
          descriptionText: triggerData.description,
          workflowType: triggerData.type || 'TRIGGER',
          status: response.data.trigger.status || 'DRAFT',
          statusValue: response.data.trigger.status || 'DRAFT',
          enrolled: "No enrollments",
          createdBy: "Unknown",
          lastUpdated: new Date().toISOString(),
          source: "Unknown",
          executions: 0,
          nodes: 0,
          flowUuid: response.data.trigger.flowUuid,
          externalFlowId: response.data.trigger.externalFlowId
        };
        
        dispatch({ type: BASE_ACTIONS.ADD_ITEM, payload: newTrigger });
        return { 
          success: true, 
          message: "Trigger created successfully",
          data: newTrigger // Include the data field!
        };
      } else {
        return { success: false, message: response.message || "Error creating trigger" };
      }
    } catch (error) {
      return { success: false, message: "Error creating trigger" };
    } finally {
      dispatch({ type: BASE_ACTIONS.SET_LOADING, payload: false });
    }
  }, [dispatch]);

  const duplicateTriggerAction = useCallback(async (triggerData, tenantUuid) => {
    try {
      dispatch({ type: BASE_ACTIONS.SET_LOADING, payload: true });
      
      // Use createTrigger to duplicate by creating a new trigger with the same data
      const response = await createTrigger({
        tenantId: tenantUuid,
        name: `${triggerData.nameText || triggerData.name} Copy`,
        description: triggerData.descriptionText || triggerData.description
      });
      
      if (response.success) {
        // Create duplicated trigger with essential fields
        const duplicatedTrigger = {
          uuid: response.data.trigger.uuid,
          name: response.data.trigger.name,
          nameText: response.data.trigger.name,
          description: triggerData.descriptionText || triggerData.description,
          descriptionText: triggerData.descriptionText || triggerData.description,
          workflowType: triggerData.workflowType || 'TRIGGER',
          status: response.data.trigger.status || 'DRAFT',
          statusValue: response.data.trigger.status || 'DRAFT',
          enrolled: triggerData.enrolledText || "No enrollments",
          createdBy: triggerData.createdBy || "Unknown",
          lastUpdated: new Date().toISOString(),
          source: triggerData.source || "Unknown",
          executions: triggerData.executions || 0,
          nodes: triggerData.nodes || 0
        };
        
        dispatch({ type: BASE_ACTIONS.ADD_ITEM, payload: duplicatedTrigger });
        return { success: true, message: "Trigger duplicated successfully" };
      } else {
        return { success: false, message: response.message || "Error duplicating trigger" };
      }
    } catch (error) {
      return { success: false, message: "Error duplicating trigger" };
    } finally {
      dispatch({ type: BASE_ACTIONS.SET_LOADING, payload: false });
    }
  }, [dispatch]);

  const archiveTriggerAction = useCallback(async (triggerData) => {
    try {
      dispatch({ type: BASE_ACTIONS.SET_LOADING, payload: true });
      
      const response = await archiveTrigger({
        tenantId: "demo-tenant",
        triggerId: triggerData.uuid,
        triggerData
      });
      
      if (response.success) {
        dispatch({ type: BASE_ACTIONS.REMOVE_ITEM, payload: triggerData.uuid });
        return { success: true, message: "Trigger archived successfully" };
      }
    } catch (error) {
      return { success: false, message: "Error archiving trigger" };
    } finally {
      dispatch({ type: BASE_ACTIONS.SET_LOADING, payload: false });
    }
  }, [dispatch]);

  const getTriggerByIdAction = useCallback((triggerId) => {
    try {
      const trigger = baseContext.data.find(t => t.uuid === triggerId);
      if (trigger) {
        return trigger;
      }
      return null;
    } catch (error) {
      return null;
    }
  }, [baseContext.data]);

  const getTriggerByNameAction = useCallback((triggerName) => {
    try {
      const trigger = baseContext.data.find(t => 
        (t.name || t.nameText)?.trim().toLowerCase() === triggerName.trim().toLowerCase()
      );
      return trigger || null;
    } catch (error) {
      return null;
    }
  }, [baseContext.data]);

  const getExistingTriggerNames = useCallback(() => {
    try {
      const names = baseContext.data
        .map(trigger => trigger.name || trigger.nameText)
        .filter(name => name && typeof name === 'string')
        .map(name => name.trim());
      
      return names;
    } catch (error) {
      return [];
    }
  }, [baseContext.data]);

  

  
  const contextValue = {
    ...baseContext,
    // Alias for backward compatibility
    triggers: baseContext.data,
    filteredTriggers: baseContext.filteredItems,
    loadTriggers: baseContext.loadItems,
    createTriggerAction,
    duplicateTriggerAction,
    archiveTriggerAction,
    getTriggerByIdAction,
    getTriggerByNameAction,
    getExistingTriggerNames,
    setPage,
    setItemsPerPage
  };

  return (
    <TriggersContext.Provider value={contextValue}>
      {children}
    </TriggersContext.Provider>
  );
};

export const useTriggers = () => {
  const context = React.useContext(TriggersContext);
  if (!context) {
    throw new Error('useTriggers must be used within a TriggersProvider');
  }
  return context;
}; 